import OpenAI from 'openai';
import { supabase } from '../lib/supabase';

/**
 * Initialize OpenAI client for embeddings
 */
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Split text into chunks for embedding
 * @param {string} text - Text to split
 * @param {number} maxTokens - Maximum tokens per chunk
 * @returns {Array<string>} Array of text chunks
 */
function splitTextIntoChunks(text, maxTokens = 500) {
  // Simple chunking by sentences (approximately 4 chars per token)
  const sentences = text?.match(/[^.!?]+[.!?]+/g) || [text];
  const chunks = [];
  let currentChunk = '';
  let currentTokens = 0;

  for (const sentence of sentences) {
    const sentenceTokens = Math.ceil(sentence?.length / 4);
    
    if (currentTokens + sentenceTokens > maxTokens && currentChunk) {
      chunks?.push(currentChunk?.trim());
      currentChunk = sentence;
      currentTokens = sentenceTokens;
    } else {
      currentChunk += sentence;
      currentTokens += sentenceTokens;
    }
  }

  if (currentChunk) {
    chunks?.push(currentChunk?.trim());
  }

  return chunks;
}

/**
 * Generate embedding for a single text using OpenAI
 * @param {string} text - Text to embed
 * @returns {Promise<number[]>} Embedding vector
 */
export async function generateEmbedding(text) {
  try {
    if (!import.meta.env?.VITE_OPENAI_API_KEY) {
      throw new Error('OpenAI API key not configured');
    }
    
    if (!text || text?.trim()?.length === 0) {
      throw new Error('Empty text provided for embedding');
    }

    const response = await openai?.embeddings?.create({
      model: 'text-embedding-3-small',
      input: text,
    });

    if (!response?.data?.[0]?.embedding) {
      throw new Error('Invalid embedding response from OpenAI');
    }

    return response?.data?.[0]?.embedding;
  } catch (error) {
    console.error('Error generating embedding:', error?.message || error);
    throw new Error(`Failed to generate embedding: ${error?.message || 'Unknown error'}`);
  }
}

/**
 * Process document and create embeddings for all chunks
 * @param {string} documentId - Document ID
 * @param {string} content - Full document content
 * @returns {Promise<Object>} Processing results
 */
export async function processDocumentEmbeddings(documentId, content) {
  try {
    if (!documentId || !content) {
      throw new Error('Document ID and content are required');
    }

    // Update document status to processing
    await supabase?.from('project_documents')?.update({ status: 'processing' })?.eq('id', documentId);

    // Split content into chunks
    const chunks = splitTextIntoChunks(content);
    
    if (!chunks || chunks?.length === 0) {
      throw new Error('No chunks generated from document content');
    }
    
    const processedChunks = [];
    const failedChunks = [];

    // Process each chunk with individual error handling
    for (let i = 0; i < chunks?.length; i++) {
      try {
        const chunk = chunks?.[i];
        const tokenCount = Math.ceil(chunk?.length / 4);

        // Generate embedding
        const embedding = await generateEmbedding(chunk);

        // Store chunk with embedding
        const { data, error } = await supabase?.from('document_chunks')?.insert({
            document_id: documentId,
            chunk_index: i,
            content: chunk,
            embedding: JSON.stringify(embedding),
            token_count: tokenCount,
            metadata: { processed_at: new Date()?.toISOString() }
          })?.select()?.single();

        if (error) throw error;
        processedChunks?.push(data);
      } catch (chunkError) {
        console.error(`Error processing chunk ${i}:`, chunkError?.message);
        failedChunks?.push({ index: i, error: chunkError?.message });
        // Continue processing remaining chunks
      }
    }

    // Update document status based on results
    if (processedChunks?.length === 0) {
      await supabase?.from('project_documents')?.update({ status: 'failed' })?.eq('id', documentId);
      throw new Error('All chunks failed to process');
    } else if (failedChunks?.length > 0) {
      // Partial success - still mark as indexed but log warnings
      console.warn(`Document ${documentId} partially processed: ${failedChunks?.length} chunks failed`);
      await supabase?.from('project_documents')?.update({ status: 'indexed' })?.eq('id', documentId);
    } else {
      await supabase?.from('project_documents')?.update({ status: 'indexed' })?.eq('id', documentId);
    }

    return {
      documentId,
      chunksProcessed: processedChunks?.length,
      chunksFailed: failedChunks?.length,
      totalTokens: processedChunks?.reduce((sum, c) => sum + c?.token_count, 0),
      errors: failedChunks
    };
  } catch (error) {
    // Update document status to failed
    await supabase?.from('project_documents')?.update({ status: 'failed' })?.eq('id', documentId);
    
    console.error('Error processing document embeddings:', error?.message || error);
    throw new Error(`Failed to process document embeddings: ${error?.message || 'Unknown error'}`);
  }
}

/**
 * Search for similar document chunks using semantic search
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise<Array>} Similar chunks with metadata
 */
export async function searchSimilarChunks(query, options = {}) {
  try {
    if (!query || query?.trim()?.length === 0) {
      console.warn('Empty query provided for similarity search');
      return [];
    }

    const {
      projectId = null,
      matchThreshold = 0.7,
      matchCount = 5
    } = options;

    // Generate embedding for query with error handling
    let queryEmbedding;
    try {
      queryEmbedding = await generateEmbedding(query);
    } catch (embeddingError) {
      console.error('Failed to generate query embedding:', embeddingError?.message);
      // Return empty results instead of failing
      return [];
    }

    if (!queryEmbedding || queryEmbedding?.length === 0) {
      console.warn('Invalid query embedding generated');
      return [];
    }

    // Call Supabase RPC function for vector similarity search
    const { data, error } = await supabase?.rpc('search_similar_chunks', {
      query_embedding: JSON.stringify(queryEmbedding),
      match_threshold: matchThreshold,
      match_count: matchCount,
      filter_project_id: projectId
    });

    if (error) {
      console.error('Supabase RPC error:', error?.message);
      // Return empty results instead of failing
      return [];
    }

    if (!data || data?.length === 0) {
      console.log('No similar chunks found for query');
      return [];
    }

    return data?.map(chunk => ({
      chunkId: chunk?.chunk_id,
      documentId: chunk?.document_id,
      documentName: chunk?.document_name,
      documentType: chunk?.document_type,
      content: chunk?.content,
      similarity: chunk?.similarity,
      chunkIndex: chunk?.chunk_index,
      metadata: chunk?.metadata
    })) || [];
  } catch (error) {
    console.error('Error searching similar chunks:', error?.message || error);
    // Return empty results instead of throwing - chat must not fail
    return [];
  }
}

/**
 * Create document citation record
 * @param {string} messageId - Message ID
 * @param {string} documentId - Document ID
 * @param {string} chunkId - Chunk ID (optional)
 * @param {number} relevanceScore - Relevance score
 */
export async function createDocumentCitation(messageId, documentId, chunkId = null, relevanceScore = 1.0) {
  try {
    if (!messageId || !documentId) {
      throw new Error('Message ID and Document ID are required');
    }

    const { data, error } = await supabase?.from('document_citations')?.insert({
        message_id: messageId,
        document_id: documentId,
        chunk_id: chunkId,
        relevance_score: relevanceScore
      })?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating document citation:', error?.message || error);
    throw new Error(`Failed to create citation: ${error?.message || 'Unknown error'}`);
  }
}

/**
 * Get citations for a message
 * @param {string} messageId - Message ID
 * @returns {Promise<Array>} Citations with document details
 */
export async function getMessageCitations(messageId) {
  try {
    const { data, error } = await supabase?.from('document_citations')?.select(`
        *,
        project_documents (*),
        document_chunks (*)
      `)?.eq('message_id', messageId)?.order('relevance_score', { ascending: false });

    if (error) throw error;

    return data?.map(citation => ({
      id: citation?.id,
      documentId: citation?.document_id,
      documentName: citation?.project_documents?.name,
      documentType: citation?.project_documents?.type,
      chunkId: citation?.chunk_id,
      chunkContent: citation?.document_chunks?.content,
      relevanceScore: citation?.relevance_score,
      createdAt: citation?.created_at
    })) || [];
  } catch (error) {
    console.error('Error getting message citations:', error);
    throw new Error('Failed to get citations');
  }
}

export const embeddingsService = {
  generateEmbedding,
  processDocumentEmbeddings,
  searchSimilarChunks,
  createDocumentCitation,
  getMessageCitations
};